import { useCallback, useMemo, useRef, useState } from 'react'
import useQuotationProductGrouping from '../quotation/useQuotationProductGrouping'
import { GROUP_QUANTITY_MODE_SET_MULTIPLIER } from '../quotation/quotationGroupingHelpers'
import {
  computeDesignOrderProductTableRenderOrder,
  createEmptyDesignOrderProductRow,
  DESIGN_ORDER_NEGATIVE_MARKETING_PERMISSIONS,
  getCatalogProductsFromRows,
  getDesignOrderLosingMoneyRowSx,
  syncCatalogProductsToRows,
} from './designOrderProductRowHelpers'

export default function useDesignOrderProductGrouping({
  productRows,
  setProductRows,
  markupType,
  theme,
  enqueueSnackbar,
  productAttachments,
  setProductAttachments,
  explodedPermissions,
  hasMarkupPermission,
  onRemapRowSideEffects,
}) {
  const [refreshCalculation, setRefreshCalculation] = useState(false)
  const remapProductIndexIdsRef = useRef(null)
  const remapGroupStateRef = useRef(null)

  const catalogProducts = useMemo(
    () => getCatalogProductsFromRows(productRows),
    [productRows],
  )

  const setCatalogProducts = useCallback(
    (updater) => {
      setProductRows((prevRows) => {
        const prevCatalog = getCatalogProductsFromRows(prevRows)
        const nextCatalog =
          typeof updater === 'function' ? updater(prevCatalog) : updater
        return syncCatalogProductsToRows(prevRows, nextCatalog)
      })
    },
    [setProductRows],
  )

  const grouping = useQuotationProductGrouping({
    selectedProducts: catalogProducts,
    setSelectedProducts: setCatalogProducts,
    markupType,
    theme,
    enqueueSnackbar,
    productAttachments,
    setProductAttachments,
    explodedPermissions,
    hasMarkupPermission,
    setRefreshCalculation,
    remapProductIndexIds: (indexIdMap, catalogProductsSnapshot) => {
      remapProductIndexIdsRef.current?.(indexIdMap, catalogProductsSnapshot)
    },
    negativeMarketingPermissionNames: DESIGN_ORDER_NEGATIVE_MARKETING_PERMISSIONS,
    groupQuantityMode: GROUP_QUANTITY_MODE_SET_MULTIPLIER,
  })

  remapGroupStateRef.current = grouping.remapGroupStateOnIndexIdChange

  const remapProductIndexIds = useCallback(
    (indexIdMap, catalogProductsSnapshot) => {
      const survivingOldCatalogIds = new Set(
        (catalogProductsSnapshot || []).map((p) => String(p.indexId)),
      )

      const snapshotByOldId = new Map(
        (catalogProductsSnapshot || []).map((p) => [String(p.indexId), p]),
      )

      setProductRows((prevRows) => {
        const remainingRows = (prevRows || []).filter((row) => {
          if (row?.entryMode === 'choose' && row?.catalogProduct) {
            return survivingOldCatalogIds.has(String(row.indexId))
          }
          return true
        })

        if (remainingRows.length === 0) {
          queueMicrotask(() => {
            onRemapRowSideEffects?.({})
            remapGroupStateRef.current?.({})
          })
          return [createEmptyDesignOrderProductRow(1)]
        }

        const sorted = [...remainingRows].sort(
          (a, b) => (Number(a?.indexId) || 0) - (Number(b?.indexId) || 0),
        )
        const fullIndexIdMap = {}
        sorted.forEach((row, idx) => {
          fullIndexIdMap[String(row.indexId)] = idx + 1
        })

        const renumbered = sorted.map((row) => {
          const newId = fullIndexIdMap[String(row.indexId)]
          const snapshotProduct = snapshotByOldId.get(String(row.indexId))
          if (!row?.catalogProduct) {
            return { ...row, indexId: newId }
          }
          const catalogProduct = {
            ...(snapshotProduct || row.catalogProduct),
            indexId: newId,
          }
          return {
            ...row,
            indexId: newId,
            catalogProduct,
            product_name:
              catalogProduct.name ||
              catalogProduct.product_name ||
              row.product_name ||
              '',
            product_desc:
              catalogProduct.description ||
              catalogProduct.product_desc ||
              row.product_desc ||
              '',
          }
        })

        queueMicrotask(() => {
          onRemapRowSideEffects?.(fullIndexIdMap)
          remapGroupStateRef.current?.(fullIndexIdMap)
        })

        return syncCatalogProductsToRows(
          renumbered,
          getCatalogProductsFromRows(renumbered),
        )
      })
    },
    [setProductRows, onRemapRowSideEffects],
  )

  remapProductIndexIdsRef.current = remapProductIndexIds

  const remapDesignOrderProductIndexIds = useCallback(
    (indexIdMap) => {
      setProductRows((prev) => {
        const remapped = (prev || []).map((row) => {
          const newId = indexIdMap[String(row.indexId)]
          if (newId === undefined) return row
          return {
            ...row,
            indexId: newId,
            ...(row.catalogProduct
              ? { catalogProduct: { ...row.catalogProduct, indexId: newId } }
              : {}),
          }
        })
        return syncCatalogProductsToRows(
          remapped,
          getCatalogProductsFromRows(remapped).map((p) => ({
            ...p,
            indexId: indexIdMap[String(p.indexId)] ?? p.indexId,
          })),
        )
      })
      onRemapRowSideEffects?.(indexIdMap)
      grouping.remapGroupStateOnIndexIdChange(indexIdMap)
    },
    [setProductRows, onRemapRowSideEffects, grouping.remapGroupStateOnIndexIdChange],
  )

  const handleDeleteGroup = useCallback(
    (groupId) => {
      grouping.handleDeleteGroup(groupId)
      const sid = String(groupId)
      grouping.setOpenGroupedRows((prev) => {
        const next = { ...(prev || {}) }
        delete next[groupId]
        delete next[sid]
        return next
      })
      grouping.setCustomerOrderProductGroups((prev) =>
        (prev || []).filter(
          (g) => String(g?.id ?? g?.group_id) !== sid,
        ),
      )
    },
    [grouping],
  )

  const productTableBodyRenderOrder = useMemo(
    () =>
      computeDesignOrderProductTableRenderOrder(
        productRows,
        grouping.groupedProducts,
      ),
    [productRows, grouping.groupedProducts],
  )

  return {
    ...grouping,
    handleDeleteGroup,
    getLosingMoneyRowSx: (isLosingMoneyItem) =>
      getDesignOrderLosingMoneyRowSx(theme, isLosingMoneyItem),
    refreshCalculation,
    setRefreshCalculation,
    catalogProducts,
    setCatalogProducts,
    productTableBodyRenderOrder,
    remapDesignOrderProductIndexIds,
  }
}
